import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Download, Sparkles, Users, TrendingUp, ArrowRight, Search, Star } from 'lucide-react'
import { useInView } from 'react-intersection-observer'
import CountUp from 'react-countup'

export default function LandingPage() {
  const navigate = useNavigate()
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  }

  return (
  <div className="min-h-screen bg-nitrr-background dark:bg-gray-950">
      {/* Modern Hero Section */}
      <section className="hero-gradient section-lg relative overflow-hidden">
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-nitrr-primary/5 rounded-full blur-3xl float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-nitrr-secondary/5 rounded-full blur-3xl" style={{animationDelay: '2s'}}></div>

        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-6 flex items-center justify-center gap-3">
              <img 
                src="/icons/nitrr.svg" 
                alt="NIT Raipur" 
                className="w-16 h-16 object-contain"
              />
              <span className="badge badge-primary badge-pulse">
                🎨 Official Logo Repository
              </span>
            </motion.div>

            {/* Hero Heading */}
            <motion.h1
              variants={itemVariants}
              className="heading-hero mb-6"
            >
              <span className="text-gradient-primary">NIT Raipur</span>
              <br />
              Logo Library
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-xl text-nitrr-text-secondary mb-8 max-w-2xl mx-auto"
            >
              Download official logos for all clubs, committees, departments, and events. 
              Available in multiple formats with instant access.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            >
              <button
                onClick={() => navigate('/all-logos')}
                className="btn-primary btn-lg hover-glow group"
              >
                <Sparkles className="group-hover:rotate-12 transition-transform" size={20} />
                View All Logos
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </button>
              
              <button
                onClick={() => navigate('/browse')}
                className="btn-secondary btn-lg"
              >
                Browse by Category
              </button>
            </motion.div>

            {/* Feature Pills */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-4 justify-center stagger-children"
            >
              {[
                { icon: <Download size={16} />, text: '25+ Logos' },
                { icon: <Users size={16} />, text: 'All Clubs' },
                { icon: <TrendingUp size={16} />, text: 'HD Quality' },
                { icon: <Star size={16} />, text: 'Free Access' },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="card-hover px-4 py-2 flex items-center gap-2 text-nitrr-text-secondary hover:text-nitrr-primary fade-in"
                >
                  {feature.icon}
                  <span className="font-medium">{feature.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={ref} className="section bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { value: 25, suffix: '+', label: 'Total Logos', icon: '🎨' },
              { value: 6, suffix: '', label: 'Categories', icon: '📁' },
              { value: 1200, suffix: '+', label: 'Downloads', icon: '⬇️' },
              { value: 500, suffix: '+', label: 'Students', icon: '👥' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="card-hover text-center p-6"
              >
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className="text-4xl font-bold text-gradient-primary mb-2">
                  {inView && (
                    <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} />
                  )}
                </div>
                <div className="text-nitrr-text-muted font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="section bg-nitrr-surface">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">
              Browse by <span className="text-gradient-primary">Category</span>
            </h2>
            <p className="text-lg text-nitrr-text-secondary">
              Organized collections for easy discovery
            </p>
          </div>

          <div className="grid-responsive stagger-children">
            {[
              {
                title: 'Technical Clubs',
                count: '3 logos',
                icon: '⚡',
                gradient: 'bg-gradient-primary'
              },
              {
                title: 'Cultural Clubs',
                count: '5 logos',
                icon: '🎭',
                gradient: 'bg-gradient-secondary'
              },
              {
                title: 'Social Clubs',
                count: '5 logos',
                icon: '🌟',
                gradient: 'bg-gradient-success'
              },
              {
                title: 'Institute',
                count: '2 logos',
                icon: '🏛️',
                gradient: 'bg-gradient-primary'
              },
            ].map((category, index) => (
              <div
                key={index}
                className="card-hover cursor-pointer group fade-in"
                onClick={() => navigate(`/category/${category.title.toLowerCase()}`)}
              >
                <div className="card-body">
                  <div className={`w-16 h-16 ${category.gradient} rounded-2xl flex items-center justify-center text-2xl text-white mb-4 group-hover:scale-110 transition-transform`}>
                    {category.icon}
                  </div>
                  <h3 className="heading-3 mb-2">{category.title}</h3>
                  <p className="text-nitrr-text-muted mb-4">{category.count}</p>
                  <div className="flex items-center text-nitrr-primary font-medium group-hover:gap-3 transition-all">
                    Browse Collection
                    <ArrowRight size={18} className="ml-1 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section bg-gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 max-w-6xl relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-6">
              <img 
                src="/icons/nitrr.svg" 
                alt="NIT Raipur Logo" 
                className="w-24 h-24 object-contain opacity-90"
              />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join hundreds of students and faculty using our logo library
            </p>
            <button
              onClick={() => navigate('/browse')}
              className="btn-secondary btn-lg hover-scale"
            >
              <Sparkles size={20} />
              Start Exploring
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
